import { DocumentDto, getBase64FromBaseString } from '@ecdlink/core';
import { DocumentInput } from '@ecdlink/graphql';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { DocumentService } from '@services/DocumentService';
import { RootState, ThunkApiType } from '../types';

export const DocumentActions = {
  CREATE_DOCUMENT: 'createDocument',
  UPDATE_DOCUMENT: 'updateDocument',
};

export const getDocumentsForHCW = createAsyncThunk<
  DocumentDto[],
  undefined,
  ThunkApiType<RootState>
>('getDocumentsForHCW', async (_, { getState, rejectWithValue }) => {
  const {
    auth: { userAuth },
    documents: { documentsForHCW: cache },
  } = getState();

  let latestDocDate: Date | null = null;

  if (cache?.length) {
    // More efficient - no sorting needed
    const latestDoc = cache.reduce((latest, doc) => {
      const docDate = new Date(doc.insertedDate!).getTime();
      const latestDate = new Date(latest.insertedDate!).getTime();
      return docDate > latestDate ? doc : latest;
    });
    latestDocDate = latestDoc.insertedDate
      ? new Date(latestDoc.insertedDate)
      : null;
  }

  try {
    if (userAuth?.auth_token) {
      return await new DocumentService(userAuth?.auth_token).getDocumentsForHCW(
        latestDocDate
      );
    } else {
      return rejectWithValue('no access token, profile check required');
    }
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const createDocument = createAsyncThunk<
  boolean[],
  DocumentDto,
  ThunkApiType<RootState>
>(
  DocumentActions.CREATE_DOCUMENT,
  async (input, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      documents: { documentsForHCW },
    } = getState();

    const documents = documentsForHCW;
    try {
      if (!input.file) return [false];

      if (userAuth?.auth_token && documents) {
        for (const document of documents) {
          if (document.file) {
            const splitString = getBase64FromBaseString(document.file);

            const _documentService = new DocumentService(userAuth?.auth_token);

            const fileReturnModel = await _documentService.fileUpload(
              splitString,
              document.name,
              document.fileType ?? ''
            );

            if (fileReturnModel) {
              const documentInputModel: DocumentInput = {
                Id: document.id,
                UserId: document.userId,
                WorkflowStatusId: document.workflowStatusId,
                DocumentTypeId: document.documentTypeId,
                Name: fileReturnModel.name,
                Reference: fileReturnModel.url,
                CreatedUserId: document.createdUserId,
                IsActive: true,
              };

              await _documentService.updateDocument(
                document.id ?? '',
                documentInputModel
              );
            }
          }
        }
      }
      return [true];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const upsertDocument = createAsyncThunk<
  boolean[],
  DocumentDto,
  ThunkApiType<RootState>
>(
  DocumentActions.UPDATE_DOCUMENT,
  async (input, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      documents: { documentsForHCW },
    } = getState();

    const documents = documentsForHCW;

    try {
      if (userAuth?.auth_token && documents) {
        for (const document of documents) {
          if ('synced' in document && !document?.synced) {
            if (document.file) {
              const splitString = getBase64FromBaseString(document.file);

              const _documentService = new DocumentService(
                userAuth?.auth_token
              );

              const fileReturnModel = await _documentService.fileUpload(
                splitString,
                document.name,
                document.fileType ?? ''
              );

              if (fileReturnModel) {
                const documentInputModel: DocumentInput = {
                  Id: document.id,
                  UserId: document.userId,
                  WorkflowStatusId: document.workflowStatusId,
                  DocumentTypeId: document.documentTypeId,
                  Name: fileReturnModel.name,
                  Reference: fileReturnModel.url,
                  CreatedUserId: document.createdUserId,
                  IsActive: true,
                };

                if ('synced' in document && document.synced === false) {
                  await _documentService.updateDocument(
                    document.id ?? '',
                    documentInputModel
                  );
                }
              }
            }
          }
        }
      }

      return [true];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
